// ==========================
// Version 1 — hooks/useUserPrefs.js
// - Subscribes to Firestore users/{uid} for live profile/prefs
// - Returns { prefs, loading, error }
// ==========================
import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

export const DEFAULT_PREFS = Object.freeze({
  isPublicProfile: false,
  defaults: { isPublic: false, isMediaPublic: false },
  notifications: { emailSummaries: true },
});

export default function useUserPrefs(uid) {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(!!uid);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setPrefs(DEFAULT_PREFS);
      setLoading(false);
      setError(null);
      return;
    }

    const ref = doc(db, 'users', uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setPrefs(DEFAULT_PREFS);
        } else {
          const data = snap.data() || {};
          setPrefs({
            isPublicProfile: !!data.isPublicProfile,
            defaults: {
              isPublic: !!data.defaults?.isPublic,
              isMediaPublic: !!data.defaults?.isMediaPublic,
            },
            notifications: {
              emailSummaries: data.notifications?.emailSummaries !== false,
            },
          });
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('[useUserPrefs] onSnapshot error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  // helpful memo if you want to read defaults directly
  const defaults = useMemo(
    () => ({
      isPublic: !!prefs.defaults?.isPublic,
      isMediaPublic: !!prefs.defaults?.isMediaPublic,
    }),
    [prefs]
  );

  return { prefs, defaults, loading, error };
}

// ==========================
// End version - Version 1 
// ==========================